import api from './apiService';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

const schemaService = {
  // Get all schemas with optional filters
  getAll: async (filters = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('schemaService: Fetching all schemas with filters:', filters);
      console.log('schemaService: Filter types:', Object.keys(filters).map(key => `${key}: ${typeof filters[key]}`));
    }

    // Build query parameters
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.page_size) params.append('page_size', filters.page_size.toString());
    if (filters.schema_type) params.append('schema_type', filters.schema_type);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `/schemas?${queryString}` : '/schemas';

    if (process.env.NODE_ENV === 'development') {
      console.log('schemaService: Making request to URL:', url);
      console.log('schemaService: Query string:', queryString);
    }

    const response = await api.get(url);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'getAll');

    // Return the data directly (should be an array of schemas)
    return apiResponse.data || [];
  },

  // Get schema by ID
  getById: async (id) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('schemaService: Fetching schema by ID:', id);
    }

    const response = await api.get(`/schemas/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'getById');

    // Return the data directly (should be a single schema object)
    return apiResponse.data;
  },

  // Create new schema
  create: async (schemaData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemaService: Creating schema with data:', schemaData);
      }

      // Create a copy of the schemaData to avoid modifying the original
      const schemaDataToSend = { ...schemaData };

      const response = await api.post('/schemas', schemaDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'create');

      // Return the data directly (should be the created schema object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('schemaService: Error creating schema');

      if (process.env.NODE_ENV === 'development') {
        console.error('schemaService: Error details:', error);
        console.error('schemaService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Update existing schema
  update: async (id, schemaData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemaService: Updating schema with ID:', id, 'and data:', schemaData);
      }

      // Create a copy of the schemaData to avoid modifying the original
      const schemaDataToSend = { ...schemaData };

      const response = await api.put(`/schemas/${id}`, schemaDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'update');

      // Return the data directly (should be the updated schema object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('schemaService: Error updating schema');

      if (process.env.NODE_ENV === 'development') {
        console.error('schemaService: Error details:', error);
        console.error('schemaService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Delete schema
  delete: async (id) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemaService: Deleting schema with ID:', id);
      }

      const response = await api.delete(`/schemas/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'delete');

      // Return the full response for delete operations (usually contains success message)
      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error('schemaService: Error deleting schema');

      if (process.env.NODE_ENV === 'development') {
        console.error('schemaService: Error details:', error);
        console.error('schemaService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // NEW: Phase 3 validation endpoint
  validateData: async (schemaId, data, validationLevel = 'strict') => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemaService: Validating data against schema:', schemaId);
        console.log('schemaService: Validation level:', validationLevel);
        console.log('schemaService: Data type:', typeof data);
      }

      const response = await api.post(`/schemas/${schemaId}/validate`, {
        validation_level: validationLevel,
        data: data
      });

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'validateData');

      // The API returns an array with one validation result, extract the first element
      const validationResults = apiResponse.data;
      if (Array.isArray(validationResults) && validationResults.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('schemaService: Extracted validation result from array:', validationResults[0]);
        }
        return validationResults[0];
      }

      // Fallback to returning the data directly if it's not an array
      if (process.env.NODE_ENV === 'development') {
        console.log('schemaService: Returning validation result directly:', validationResults);
      }
      return validationResults;
    } catch (error) {
      // Always log errors, even in production
      console.error('schemaService: Error validating data against schema');

      if (process.env.NODE_ENV === 'development') {
        console.error('schemaService: Error details:', error);
        console.error('schemaService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // NEW: Get schema complexity metrics
  getSchemaComplexity: (schemaDefinition) => {
    if (!schemaDefinition || typeof schemaDefinition !== 'object') {
      return { count: 0, maxDepth: 0 };
    }

    const countProperties = (obj, depth = 0) => {
      let count = 0;
      let maxDepth = depth;

      if (obj && typeof obj === 'object') {
        if (obj.properties) {
          count += Object.keys(obj.properties).length;
          Object.values(obj.properties).forEach(prop => {
            const result = countProperties(prop, depth + 1);
            count += result.count;
            maxDepth = Math.max(maxDepth, result.maxDepth);
          });
        }

        // Handle array items
        if (obj.items) {
          const result = countProperties(obj.items, depth + 1);
          count += result.count;
          maxDepth = Math.max(maxDepth, result.maxDepth);
        }

        // Handle oneOf, anyOf, allOf
        ['oneOf', 'anyOf', 'allOf'].forEach(keyword => {
          if (obj[keyword] && Array.isArray(obj[keyword])) {
            obj[keyword].forEach(subSchema => {
              const result = countProperties(subSchema, depth + 1);
              count += result.count;
              maxDepth = Math.max(maxDepth, result.maxDepth);
            });
          }
        });
      }

      return { count, maxDepth };
    };

    return countProperties(schemaDefinition);
  },

  // Transform XML to JSON using backend xml_loader logic
  transformXmlToJson: async (xmlData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemaService: Transforming XML to JSON');
        console.log('schemaService: XML data length:', xmlData?.length || 0);
      }

      const response = await api.post('/schemas/utils/xml-to-json', {
        xml_data: xmlData
      });

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'schemaService', 'transformXmlToJson');

      // Return the transformed JSON data
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('schemaService: Error transforming XML to JSON');

      if (process.env.NODE_ENV === 'development') {
        console.error('schemaService: Error details:', error);
        console.error('schemaService: Error response data:', error.response?.data);
      }

      throw error;
    }
  }
};

export default schemaService;
